/**
 * TradeStack - Estimates List Page
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import './Documents.css';

function Estimates() {
  const { formatCurrency, formatDate } = useConfig();
  const [searchParams, setSearchParams] = useSearchParams();

  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const statusFilter = searchParams.get('status') || '';
  const page = parseInt(searchParams.get('page')) || 1;

  useEffect(() => {
    loadEstimates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page]);

  const loadEstimates = async () => {
    try {
      setLoading(true);
      const response = await api.getEstimates({
        status: statusFilter,
        limit: 20,
        offset: (page - 1) * 20
      });
      setEstimates(response.data.estimates || []);
    } catch (err) {
      setError('Failed to load estimates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilter = (status) => {
    const params = new URLSearchParams(searchParams);
    if (status) {
      params.set('status', status);
    } else {
      params.delete('status');
    }
    setSearchParams(params);
  };

  const statuses = [
    { id: 'draft', label: 'Draft' },
    { id: 'sent', label: 'Sent' },
    { id: 'viewed', label: 'Viewed' },
    { id: 'accepted', label: 'Accepted' },
    { id: 'declined', label: 'Declined' },
    { id: 'expired', label: 'Expired' }
  ];

  return (
    <div className="documents-page">
      <div className="page-header">
        <h1>Estimates</h1>
        <Link to="/estimates/new" className="btn btn-primary">+ New Estimate</Link>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="status-filters">
          <button
            className={`filter-btn ${!statusFilter ? 'active' : ''}`}
            onClick={() => handleStatusFilter('')}
          >
            All
          </button>
          {statuses.map(status => (
            <button
              key={status.id}
              className={`filter-btn ${statusFilter === status.id ? 'active' : ''}`}
              onClick={() => handleStatusFilter(status.id)}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {/* Estimates List */}
      {loading ? (
        <div className="loading-state">Loading estimates...</div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : estimates.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3>No estimates found</h3>
          <p>Create your first estimate to start winning jobs</p>
          <Link to="/estimates/new" className="btn btn-primary">Create Estimate</Link>
        </div>
      ) : (
        <div className="documents-table">
          <table>
            <thead>
              <tr>
                <th>Estimate #</th>
                <th>Customer</th>
                <th>Job</th>
                <th>Date</th>
                <th>Expires</th>
                <th>Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {estimates.map(estimate => (
                <tr key={estimate.id}>
                  <td>
                    <Link to={`/estimates/${estimate.id}`} className="doc-link">
                      {estimate.estimate_number}
                    </Link>
                  </td>
                  <td>{estimate.customer_name || '-'}</td>
                  <td>{estimate.job_title || '-'}</td>
                  <td>{formatDate(estimate.estimate_date)}</td>
                  <td>{estimate.expiry_date ? formatDate(estimate.expiry_date) : '-'}</td>
                  <td className="amount">{formatCurrency(estimate.total)}</td>
                  <td>
                    <span className={`status-badge status-${estimate.status}`}>
                      {estimate.status}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <Link to={`/estimates/${estimate.id}`} className="action-btn">View</Link>
                      {estimate.status === 'accepted' && (
                        <Link to={`/invoices/new?estimateId=${estimate.id}`} className="action-btn primary">
                          Invoice
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Estimates;
